'use client';

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  User,
  LogOut,
  Settings,
  Shield,
  Copy,
  Check,
  ExternalLink,
  Wallet,
} from 'lucide-react';
import { useAuthStore } from '../../stores';
import { usePassportSSO } from '../../hooks/use-passport-sso';
import { toast } from 'sonner';
import { getUserAvatar } from '../../config/media-defaults';

/**
 * User Menu Component
 * Shows connected wallet address and user options after Passport SSO login
 */
export function UserMenu() {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);

  // Get user data from auth store
  const { walletAddress, user, isAuthenticated, clearAuth } = useAuthStore();
  const { passportLogout, redirectToLogin } = usePassportSSO();

  // Format wallet address for display (0x1234...5678)
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Copy address to clipboard
  const copyAddress = async () => {
    if (!walletAddress) return;
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      toast.success('Address copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy address');
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await passportLogout();
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Logout failed');
    }
  };

  // Handle switch wallet - redirect to passport portal
  const handleSwitchWallet = () => {
    // Redirect to passport portal to switch wallet/provider
    window.location.href = `${import.meta.env.VITE_PASSPORT_URL || 'https://auth.tracverse.com'}/account/providers`;
  };

  if (!isAuthenticated || !walletAddress) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex items-center gap-2 px-3 py-2 h-auto rounded-full border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 transition-all"
        >
          {/* Avatar */}
          <Avatar className="h-7 w-7">
            <AvatarImage src={getUserAvatar(user)} alt="User avatar" />
            <AvatarFallback className="bg-gradient-to-br from-violet-500 to-cyan-500 text-white text-xs">
              {user?.username?.slice(0, 2)?.toUpperCase() || walletAddress?.slice(2, 4)?.toUpperCase()}
            </AvatarFallback>
          </Avatar>

          {/* Wallet Address */}
          <span className="text-sm font-medium text-white hidden sm:inline">
            {formatAddress(walletAddress)}
          </span>

          {/* Mobile: Just show icon */}
          <Wallet className="h-4 w-4 text-white sm:hidden" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-64 bg-zinc-900 border border-white/10 shadow-xl"
      >
        {/* User Info Header */}
        <DropdownMenuLabel className="px-3 py-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={getUserAvatar(user)} alt="User avatar" />
              <AvatarFallback className="bg-gradient-to-br from-violet-500 to-cyan-500 text-white">
                {user?.username?.slice(0, 2)?.toUpperCase() || walletAddress?.slice(2, 4)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-white">
                {user?.username || 'Anonymous'}
              </span>
              <button
                onClick={copyAddress}
                className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition-colors"
              >
                {formatAddress(walletAddress)}
                {copied ? (
                  <Check className="h-3 w-3 text-green-400" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </button>
            </div>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator className="bg-white/10" />

        {/* Profile Link */}
        <DropdownMenuItem asChild>
          <Link
            to={`/profile/${user?.passportId || user?.oid || walletAddress}`}
            className="flex items-center gap-2 px-3 py-2 cursor-pointer text-zinc-300 hover:text-white hover:bg-white/5"
          >
            <User className="h-4 w-4" />
            <span>My Profile</span>
          </Link>
        </DropdownMenuItem>

        {/* Passport Account Settings */}
        <DropdownMenuItem
          onClick={() => window.open(`${import.meta.env.VITE_PASSPORT_URL || 'https://auth.tracverse.com'}/account`, '_blank')}
          className="flex items-center gap-2 px-3 py-2 cursor-pointer text-zinc-300 hover:text-white hover:bg-white/5"
        >
          <Shield className="h-4 w-4" />
          <span>Passport Settings</span>
          <ExternalLink className="h-3 w-3 ml-auto text-zinc-500" />
        </DropdownMenuItem>

        {/* Switch Wallet/Provider */}
        <DropdownMenuItem
          onClick={handleSwitchWallet}
          className="flex items-center gap-2 px-3 py-2 cursor-pointer text-zinc-300 hover:text-white hover:bg-white/5"
        >
          <Wallet className="h-4 w-4" />
          <span>Switch Wallet</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-white/10" />

        {/* Logout */}
        <DropdownMenuItem
          onClick={handleLogout}
          className="flex items-center gap-2 px-3 py-2 cursor-pointer text-red-400 hover:text-red-300 hover:bg-red-500/10"
        >
          <LogOut className="h-4 w-4" />
          <span>Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default UserMenu;
